import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Group,
  InlineError,
  Input,
  PageHeader,
  PageRouteScaffold,
  Paper,
  Stack,
  Text,
  TextArea,
} from '@wapharma/design-system';
import { unwrapData } from '@wapharma/shared';
import {
  DEFAULT_STOREFRONT_SITE_SETTINGS,
  STOREFRONT_SITE_SETTINGS_KEY,
  mergeStorefrontSiteSettings,
  type StorefrontSiteSettings,
} from '@wapharma/shared';
import { apiGet, apiPut } from '../api';
import { getToken } from '../auth';
import { useToast } from '../contexts/ToastContext';
import { getDisplayError } from '../apiErrors';
import { useStaffRoles } from '../hooks/useStaffRoles';

type SettingRow = { key: string; value: string };

export function StorefrontSiteSettingsPage({
  embedded = false,
  onSaved,
}: {
  embedded?: boolean;
  onSaved?: () => void;
} = {}) {
  const token = getToken();
  const { showToast } = useToast();
  const { canConfigureCommerce } = useStaffRoles();
  const [draft, setDraft] = useState<StorefrontSiteSettings>(DEFAULT_STOREFRONT_SITE_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet<{ data: SettingRow | null }>(
        `/admin/settings/${encodeURIComponent(STOREFRONT_SITE_SETTINGS_KEY)}`,
        token,
      );
      const row = unwrapData(res) as SettingRow | null;
      if (row?.value?.trim()) {
        setDraft(mergeStorefrontSiteSettings(JSON.parse(row.value) as Partial<StorefrontSiteSettings>));
      } else {
        setDraft({ ...DEFAULT_STOREFRONT_SITE_SETTINGS });
      }
    } catch (e) {
      setError(getDisplayError(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = (p: Partial<StorefrontSiteSettings>) => setDraft((prev) => ({ ...prev, ...p }));

  const save = async () => {
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      await apiPut(
        `/admin/settings/${encodeURIComponent(STOREFRONT_SITE_SETTINGS_KEY)}`,
        { value: JSON.stringify(draft, null, 2) },
        token,
      );
      showToast('Paramètres site enregistrés.', { tone: 'success' });
      onSaved?.();
    } catch (e) {
      const msg = getDisplayError(e);
      setError(msg);
      showToast(msg, { tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const commerceDisabled = loading || !canConfigureCommerce;

  const body = (
    <>
      {error ? <InlineError>{error}</InlineError> : null}
      <Stack gap="lg" style={{ maxWidth: '42rem' }}>
        {!embedded ? null : (
          <Group justify="flex-end">
            <Button type="button" variant="primary" loading={saving} onClick={() => void save()} disabled={loading}>
              Enregistrer
            </Button>
          </Group>
        )}
        <Paper p="md">
          <Stack gap="sm">
            <Input label="Raison sociale" value={draft.legalName} onChange={(e) => patch({ legalName: e.target.value })} disabled={loading} />
            <Input label="Nom affiché" value={draft.displayName} onChange={(e) => patch({ displayName: e.target.value })} disabled={loading} />
            <Input label="Adresse" value={draft.addressLine1} onChange={(e) => patch({ addressLine1: e.target.value })} disabled={loading} />
            <Input label="Ville" value={draft.city} onChange={(e) => patch({ city: e.target.value })} disabled={loading} />
            <Input label="ICE" value={draft.ice} onChange={(e) => patch({ ice: e.target.value })} disabled={loading} />
            <Input label="RC" value={draft.rc} onChange={(e) => patch({ rc: e.target.value })} disabled={loading} />
          </Stack>
        </Paper>
        <Paper p="md">
          <Stack gap="sm">
            <Input label="E-mail contact" value={draft.email} onChange={(e) => patch({ email: e.target.value })} disabled={loading} />
            <Input label="E-mail commandes" value={draft.ordersEmail} onChange={(e) => patch({ ordersEmail: e.target.value })} disabled={loading} />
            <Input label="Téléphone (E.164)" value={draft.phone} onChange={(e) => patch({ phone: e.target.value })} disabled={loading} />
            <Input label="Téléphone affiché" value={draft.phoneDisplay} onChange={(e) => patch({ phoneDisplay: e.target.value })} disabled={loading} />
            <Input label="WhatsApp" value={draft.whatsapp} onChange={(e) => patch({ whatsapp: e.target.value })} disabled={loading} />
          </Stack>
        </Paper>
        <Paper p="md">
          <Stack gap="sm">
            {!canConfigureCommerce ? (
              <Text size="sm" c="dimmed">
                Seuils commerce (livraison offerte, retours) : capacité livraison/paiement requise.
              </Text>
            ) : null}
            <Input
              label="Livraison offerte dès (MAD)"
              type="number"
              min={1}
              value={String(draft.freeShippingThresholdMad)}
              onChange={(e) =>
                patch({ freeShippingThresholdMad: Number(e.target.value) || draft.freeShippingThresholdMad })
              }
              disabled={commerceDisabled}
            />
            <Input
              label="Délai de rétractation (jours)"
              type="number"
              min={1}
              value={String(draft.withdrawalDays)}
              onChange={(e) =>
                patch({ withdrawalDays: Number(e.target.value) || draft.withdrawalDays })
              }
              disabled={commerceDisabled}
            />
            <Input
              label="Délai de remboursement (jours)"
              type="number"
              min={1}
              value={String(draft.refundDays)}
              onChange={(e) => patch({ refundDays: Number(e.target.value) || draft.refundDays })}
              disabled={commerceDisabled}
            />
          </Stack>
        </Paper>
        <Paper p="md">
          <Stack gap="sm">
            <Input label="SEO — titre par défaut" value={draft.seoTitleDefault} onChange={(e) => patch({ seoTitleDefault: e.target.value })} disabled={loading} />
            <TextArea
              label="SEO — description par défaut"
              value={draft.seoDescriptionDefault}
              onChange={(e) => patch({ seoDescriptionDefault: e.target.value })}
              disabled={loading}
              rows={3}
            />
            <Input label="Instagram" value={draft.socialInstagram ?? ''} onChange={(e) => patch({ socialInstagram: e.target.value })} disabled={loading} />
            <Input label="Facebook" value={draft.socialFacebook ?? ''} onChange={(e) => patch({ socialFacebook: e.target.value })} disabled={loading} />
          </Stack>
        </Paper>
      </Stack>
    </>
  );

  if (embedded) return body;

  return (
    <PageRouteScaffold
      band={
        <PageHeader
          title="Site vitrine — identité & SEO"
          breadcrumbs={[{ label: 'Accueil', href: '/' }, { label: 'Site vitrine' }]}
          variant="dock"
          end={
            <Button type="button" variant="primary" loading={saving} onClick={() => void save()} disabled={loading}>
              Enregistrer
            </Button>
          }
        />
      }
    >
      {body}
    </PageRouteScaffold>
  );
}
